/**
 * Buy with Shop / Dynamic Checkout — Quantity Split Handler
 *
 * Why: Shopify's `{{ form | payment_button }}` renders Shop Pay / Buy with Shop /
 * PayPal / Apple Pay / Google Pay buttons that bypass the cart entirely. They
 * post one variant + quantity directly to checkout, so cart-side splitting
 * (cart-attributes-cleanup.js -> splitMergedCustomItemsOnCart) never runs and
 * customers reach checkout with one merged line of qty N.
 *
 * Approach: capture clicks on the payment-button area BEFORE Shopify's own
 * handlers fire. If qty > 1, prevent default, push N x 1 split lines to the
 * cart with unique LineUUIDs (matching the existing convention so each line
 * stays distinct), then redirect to /checkout. Shop Pay-recognised customers
 * still get autofill at /checkout, so the express UX is preserved.
 */
(function () {
  'use strict';

  // Target ONLY real dynamic-checkout buttons:
  //   - <shop-pay-button>           web component for "Buy with Shop"
  //   - .shopify-payment-button__button   BEM child class — only present on
  //     buttons rendered by Shopify's payment_button filter / dynamic checkout.
  // Do NOT match the bare class `.shopify-payment-button` because the theme's
  // upsell button (snippets/upsell-product.liquid) borrows it as a styling
  // class on a plain Add-to-Cart submit button — intercepting that would
  // wrongly redirect upsell add-to-cart clicks to /checkout.
  var SHOP_PAY_TAG = 'shop-pay-button';
  var PAYMENT_BUTTON_BEM = '.shopify-payment-button__button';
  var FORM_SELECTOR = 'form[action*="/cart/add"]';
  var FEEDBACK_ID = 'shop-pay-split-feedback';

  var splitInProgress = false;

  function extractFormData(form) {
    var fd = new FormData(form);
    var variantId = fd.get('id');
    var qtyRaw = fd.get('quantity');
    var qty = parseInt(qtyRaw || '1', 10);
    if (!qty || qty < 1) qty = 1;

    var properties = {};
    var sellingPlan = fd.get('selling_plan');

    fd.forEach(function (value, key) {
      var m = key.match(/^properties\[([^\]]+)\]$/);
      if (m && value !== '' && value != null) {
        properties[m[1]] = value;
      }
    });

    return {
      variantId: variantId,
      qty: qty,
      properties: properties,
      sellingPlan: sellingPlan
    };
  }

  function buildSplitItems(data) {
    var items = [];
    var t = Date.now();
    for (var i = 0; i < data.qty; i++) {
      var uuid = data.variantId + '-' + t + '-' + i + '-' + Math.random().toString(36).slice(2, 8);
      var props = Object.assign({}, data.properties, { LineUUID: uuid });
      var item = { id: data.variantId, quantity: 1, properties: props };
      if (data.sellingPlan) item.selling_plan = data.sellingPlan;
      items.push(item);
    }
    return items;
  }

  function showFeedback(text) {
    var f = document.getElementById(FEEDBACK_ID);
    if (!f) {
      f = document.createElement('div');
      f.id = FEEDBACK_ID;
      f.style.cssText =
        'position:fixed;bottom:20px;left:50%;transform:translateX(-50%);' +
        'background:#000;color:#fff;padding:10px 20px;border-radius:5px;' +
        'z-index:99999;font-weight:bold;font-family:sans-serif;';
      document.body.appendChild(f);
    }
    f.innerText = text;
    f.style.display = 'block';
  }

  function hideFeedback() {
    var f = document.getElementById(FEEDBACK_ID);
    if (f) f.style.display = 'none';
  }

  function isPaymentButtonTarget(target) {
    if (!target || !target.closest) return false;
    if (target.closest(SHOP_PAY_TAG)) return true;
    if (target.closest(PAYMENT_BUTTON_BEM)) return true;
    return false;
  }

  function handleClick(e) {
    if (splitInProgress) {
      e.preventDefault();
      e.stopImmediatePropagation();
      return;
    }
    if (!isPaymentButtonTarget(e.target)) return;

    var form = e.target.closest(FORM_SELECTOR);
    if (!form) return;

    var data = extractFormData(form);
    if (!data.variantId || data.qty <= 1) return;

    e.preventDefault();
    e.stopImmediatePropagation();

    splitInProgress = true;
    showFeedback('Preparing your items...');

    var items = buildSplitItems(data);

    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ items: items })
    })
      .then(function (res) {
        if (!res.ok) throw new Error('cart/add failed: ' + res.status);
        return res.json();
      })
      .then(function () {
        showFeedback('Redirecting to checkout...');
        window.location.href = '/checkout';
      })
      .catch(function (err) {
        console.error('[shop-pay-split] failed:', err);
        splitInProgress = false;
        hideFeedback();
        try { form.submit(); } catch (_) {}
      });
  }

  document.addEventListener('click', handleClick, true);
})();
